import React from 'react';
import { Link } from 'react-router-dom';
import { useFavorites } from '../../context/FavoritesContext';
import { useCart } from '../../context/CartContext';
import { Trash2, ShoppingCart, ArrowLeft, Heart } from 'lucide-react';
import Button from '../common/Button';
import Toast from '../common/Toast';

const FavoritesPage = () => {
    const { favorites, toggleFavorite, clearFavorites } = useFavorites();
    const { addToCart } = useCart();
    const [showToast, setShowToast] = React.useState(false);
    const [toastMessage, setToastMessage] = React.useState('');

    const handleAddToCart = (product) => {
        const wasAdded = addToCart(product);
        if (wasAdded) {
            setToastMessage(`${product.name} ajouté au panier !`);
        } else {
            setToastMessage("Ce produit est déjà dans votre panier.");
        }
        setShowToast(true);
    };

    if (favorites.length === 0) {
        return (
            <div className="pt-40 pb-20 min-h-screen bg-slate-50 flex flex-col items-center justify-center px-6">
                <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-xl mb-8">
                    <Heart className="w-10 h-10 text-slate-200" />
                </div>
                <h1 className="text-3xl font-bold text-brand-dark mb-4">Vos favoris sont vides</h1>
                <p className="text-slate-500 mb-10 text-center max-w-md">
                    Enregistrez les articles que vous aimez pour les retrouver facilement ici.
                </p>
                <Link to="/">
                    <Button variant="primary" className="h-14 px-10">
                        <ArrowLeft className="w-5 h-5 mr-2" />
                        Découvrir nos produits
                    </Button>
                </Link>
            </div>
        );
    }

    return (
        <div className="pt-40 pb-24 min-h-screen bg-slate-50">
            <div className="container mx-auto px-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
                    <div>
                        <h1 className="text-4xl font-extrabold text-brand-dark mb-2">Mes Favoris</h1>
                        <p className="text-slate-500">{favorites.length} articles enregistrés</p>
                    </div>
                    <button
                        onClick={clearFavorites}
                        className="text-sm font-bold text-red-500 hover:text-red-600 transition-colors flex items-center gap-2"
                    >
                        <Trash2 className="w-4 h-4" />
                        Tout effacer
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {favorites.map((product) => (
                        <div key={product.id} className="bg-white rounded-[2.5rem] p-6 shadow-sm hover:shadow-xl transition-all border border-slate-100 group">
                            <div className="relative aspect-square rounded-[2rem] overflow-hidden mb-6">
                                <img
                                    src={product.image}
                                    alt={product.name}
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                />
                                <button
                                    onClick={() => toggleFavorite(product)}
                                    className="absolute top-4 right-4 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg text-brand hover:scale-110 transition-all"
                                >
                                    <Heart className="w-6 h-6 fill-current" />
                                </button>
                            </div>

                            <div className="mb-6">
                                <Link to={`/product/${product.id}`}>
                                    <h3 className="text-xl font-bold text-brand-dark group-hover:text-brand transition-colors mb-2">
                                        {product.name}
                                    </h3>
                                </Link>
                                <div className="mt-4 flex items-center justify-between">
                                    <span className="text-xl font-bold text-green-700">
                                        {Math.floor(product.price).toLocaleString('fr-FR')} <span className="text-xs font-bold text-slate-400">FCFA</span>
                                    </span>
                                </div>
                            </div>

                            <button
                                onClick={() => handleAddToCart(product)}
                                className="w-full h-14 bg-brand/5 text-brand-dark border-2 border-brand/10 rounded-2xl text-sm font-bold hover:bg-brand-dark hover:text-white hover:border-brand-dark transition-all duration-500 flex items-center justify-center gap-3"
                            >
                                <ShoppingCart className="w-5 h-5" />
                                Ajouter au Panier
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            <Toast
                message={toastMessage}
                isOpen={showToast}
                onClose={() => setShowToast(false)}
            />
        </div>
    );
};

export default FavoritesPage;
